import React, { useState } from 'react';
import T from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import Prospecting, { TIER_DECLINED_ERROR } from 'App/babel/resources/Prospecting';
import { getInitials } from 'App/helpers/getInitials';
import { Button } from 'App/shadcn/ui/button';
import { cn } from 'App/shadcn/lib/utils';
import { openEditContact } from 'App/components/EditContact/openEditContact';
import { notifyAIWorkspaceError } from '../../AIWorkspace/notifyAIWorkspaceError';
import { formatCredits } from 'App/shadcn/pages/AIWorkspace/Admin/workspaceSettingsShared';
import { CARD, PILL, ValueRow } from './LookupParts';
import type { LookupCompany, LookupPerson, PhoneOnDemand } from './lookupApi';

type Props = {
	person: LookupPerson;
	company: LookupCompany | null;
	aiCredits: number;
	onLookUp: (query: string) => void;
	/** Absent where the lookup already asked for the phone. */
	phone?: PhoneOnDemand;
};

const splitName = (name: string) => {
	const [firstName = '', ...rest] = name.trim().split(/\s+/).filter(Boolean);
	return { firstName, lastName: rest.join(' ') };
};

type CrmClient = { id: number; name: string };

/**
 * The existing contact modal, filled in with what the lookup found. Under the CRM company when there
 * is one; without it the modal asks which company, and can add one (WEB-13921). The found number is
 * saved as the mobile, as the Chrome extension saves it.
 */
const addAsContact = (person: LookupPerson, client: CrmClient | null) => {
	const name = person.name ?? '';
	openEditContact({
		contact: {
			name,
			...splitName(name),
			title: person.title ?? '',
			email: person.email?.value ?? '',
			cellPhone: person.phoneNumber?.value ?? '',
			linkedin: person.linkedinUrl ?? '',
			...(client ? { client } : {})
		},
		...(client ? { account: client } : {})
	}).catch((err: unknown) => {
		// The modal rejects with nothing when the rep closes it without saving.
		if (err) notifyAIWorkspaceError(err, 'lookupAddContact');
	});
};

const PersonCard = ({ person, company, aiCredits, onLookUp, phone }: Props) => {
	// A register company the rep just added stands in for the CRM match the lookup didn't have.
	const [addedClient, setAddedClient] = useState<CrmClient | null>(null);
	const [addingCompany, setAddingCompany] = useState(false);
	const crmCompany: CrmClient | null = company?.crm
		? { id: company.crm.clientId, name: company.crm.name }
		: addedClient;
	// The register knows the company and the CRM doesn't: it can be added the way supersearch adds one.
	const registerOnly = !crmCompany && company?.source === 'register' ? company.prospectingId : null;
	const subtitle = [person.title, person.companyName, company?.firmographics?.city].filter(Boolean).join(' · ');
	// What the lookup recognises as a company; a register name alone is not one.
	const companyQuery = company?.domain ?? company?.orgNumber ?? null;
	// Who it is, even when a provider named nobody: never a header that reads as the company's.
	const heading = person.name ?? person.email?.value ?? null;

	const addCompanyAndContact = async (prospectingId: string) => {
		setAddingCompany(true);
		try {
			const saved = await Prospecting.save({ prospectingId });
			const client = { id: saved.data.id, name: saved.data.name };
			setAddedClient(client);
			addAsContact(person, client);
		} catch (err) {
			if (err !== TIER_DECLINED_ERROR) notifyAIWorkspaceError(err, 'lookupAddCompany');
		} finally {
			setAddingCompany(false);
		}
	};

	return (
		<section className={CARD} aria-label={heading ?? undefined}>
			<div className="flex flex-wrap items-center gap-[14px] border-b border-grey-4 p-5">
				<div
					aria-hidden
					className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-light text-headline-xs text-brand"
				>
					{getInitials({ name: heading ?? '' }).toUpperCase()}
				</div>
				<div className="min-w-0 flex-[1_1_200px]">
					<h2 className="text-headline-xs text-brand">{heading}</h2>
					{subtitle ? <p className="text-body-md text-grey-11">{subtitle}</p> : null}
				</div>
				{crmCompany ? (
					<span className={cn(PILL, 'bg-success-light text-success-dark')}>
						{T('prospectingTools.lookup.crm.companyInCrm')}
					</span>
				) : null}
				<span
					className={cn(
						PILL,
						person.crm ? 'bg-success-light text-success-dark' : 'bg-alert-light text-alert-dark'
					)}
				>
					{person.crm
						? T('prospectingTools.lookup.crm.alreadyContact')
						: T('prospectingTools.lookup.crm.notContact')}
				</span>
			</div>
			<dl className="m-0">
				{person.email ? (
					<ValueRow
						label={T('prospectingTools.lookup.channel.email')}
						value={person.email.value}
						found={person.email}
					/>
				) : null}
				{person.phoneNumber ? (
					<ValueRow
						label={T('voice.phoneNumber')}
						value={person.phoneNumber.value}
						found={person.phoneNumber}
					/>
				) : phone ? (
					<div className="flex flex-col gap-1 border-b border-grey-2 px-5 py-3 sm:grid sm:grid-cols-[minmax(110px,160px)_minmax(0,1fr)] sm:items-center sm:gap-4">
						<dt className="text-body-md text-grey-11">{T('voice.phoneNumber')}</dt>
						<dd className="m-0 text-body-md text-grey-11">
							{phone.state === 'asked' ? (
								T('prospectingTools.lookup.phoneNotFound')
							) : (
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={phone.state === 'finding'}
									onClick={phone.onFind}
								>
									{phone.state === 'finding'
										? T('prospectingTools.lookup.loading')
										: T('prospectingTools.lookup.findPhoneNumber')}
								</Button>
							)}
						</dd>
					</div>
				) : null}
				{person.linkedinUrl ? (
					<ValueRow label={T('default.linkedin')} value={person.linkedinUrl} href={person.linkedinUrl} />
				) : null}
			</dl>
			<div className="flex flex-wrap items-center gap-2 px-5 py-4">
				{person.crm ? (
					<Button variant="outline" asChild>
						<a href={`/#/${AppService.getCustomerId()}/contacts/${person.crm.contactId}/`}>
							{T('prospectingAgent.crmOpenIn')}
						</a>
					</Button>
				) : registerOnly ? (
					<>
						<Button
							type="button"
							disabled={addingCompany}
							onClick={() => addCompanyAndContact(registerOnly)}
						>
							{addingCompany
								? T('prospectingTools.lookup.loading')
								: T('prospectingTools.lookup.addCompanyAndContact')}
						</Button>
						<Button type="button" variant="outline" onClick={() => addAsContact(person, null)}>
							{T('prospectingTools.lookup.addAsContact')}
						</Button>
					</>
				) : (
					<Button type="button" onClick={() => addAsContact(person, crmCompany)}>
						{T('prospectingTools.lookup.addAsContact')}
					</Button>
				)}
				{/* A company already in the CRM is opened there, not looked up again, and in a new tab so the
				    lookup stays open (WEB-13771). */}
				{crmCompany ? (
					<Button variant="outline" asChild>
						<a
							href={`/#/${AppService.getCustomerId()}/accounts/${crmCompany.id}/`}
							target="_blank"
							rel="noopener noreferrer"
						>
							{T('prospectingTools.lookup.openCompany')}
						</a>
					</Button>
				) : companyQuery ? (
					<Button type="button" variant="outline" onClick={() => onLookUp(companyQuery)}>
						{T('prospectingTools.lookup.lookUpCompany')}
					</Button>
				) : null}
				<span className="flex-1" />
				<span className="text-body-sm text-grey-11">
					{T('prospectingTools.lookup.aiCredits', { count: formatCredits(aiCredits) })}
				</span>
			</div>
		</section>
	);
};

export default PersonCard;
